import React from 'react';

// ── Helpers navigation dot-notation ────────────────────────────────────────
function getNestedValue(obj, path) {
  return path.split('.').reduce((cur, k) => cur?.[k], obj);
}

function setNestedValue(obj, path, value) {
  const keys = path.split('.');
  const result = { ...obj };
  let cur = result;
  for (let i = 0; i < keys.length - 1; i++) {
    cur[keys[i]] = { ...(cur[keys[i]] || {}) };
    cur = cur[keys[i]];
  }
  cur[keys[keys.length - 1]] = value;
  return result;
}

const pct = (v) => `${(v * 100).toFixed(3)}%`;

// ── PDFField : rendu d'un champ selon son type ──────────────────────────────
function PDFField({ field, value, editable, onChange, annMap }) {
  const fontSize = field.font_size || 10;

  const inputStyle = {
    width: '100%',
    height: '100%',
    fontSize: `${fontSize}px`,
    padding: '1px 2px',
    background: 'rgba(255,255,255,0.85)',
    border: '1px solid rgba(99,102,241,0.5)',
    borderRadius: '2px',
    boxSizing: 'border-box',
    lineHeight: 1.2,
    outline: 'none',
    fontFamily: 'inherit',
  };

  const readStyle = {
    width: '100%',
    height: '100%',
    fontSize: `${fontSize}px`,
    padding: '1px 2px',
    background: 'transparent',
    border: 'none',
    boxSizing: 'border-box',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    lineHeight: 1.2,
    display: 'flex',
    alignItems: 'center',
    fontFamily: 'inherit',
  };

  const type = field.field_type;

  // ── Checkbox ──
  if (type === 'checkbox') {
    const checked = !!value;
    if (editable) {
      return (
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            style={{ width: '14px', height: '14px', cursor: 'pointer', accentColor: '#6366f1' }}
          />
        </div>
      );
    }
    return (
      <div style={{ ...readStyle, justifyContent: 'center', fontSize: `${fontSize + 2}px` }}>
        {checked ? '✓' : '✗'}
      </div>
    );
  }

  // ── Multiline ──
  if (type === 'multiline') {
    if (editable) {
      return (
        <textarea
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          style={{ ...inputStyle, resize: 'none', overflow: 'auto', lineHeight: 1.3 }}
        />
      );
    }
    return (
      <div style={{ ...readStyle, whiteSpace: 'pre-wrap', overflow: 'auto', alignItems: 'flex-start' }}>
        {value ?? ''}
      </div>
    );
  }

  // ── Resource bar ──
  if (type === 'resource_bar') {
    const maxKey = field.resource_max_key;
    // Note: onChange pour resource_bar est appelé différemment — voir PDFFieldOverlay
    // On s'attend à recevoir une onChange(newFormData) depuis le parent
    // Mais ici on veut onChange(currentValue) et onChange(maxValue) séparément
    // Le parent (PDFFieldOverlay) passe une onChange qui prend newFormData directement
    // On utilise un pattern différent pour resource_bar (voir PDFFieldOverlay)
    const displayVal = value ?? 0;
    if (editable) {
      return (
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', gap: '2px' }}>
          <input
            type="number"
            value={displayVal}
            onChange={(e) => onChange({ type: 'current', value: Number(e.target.value) })}
            style={{ ...inputStyle, width: '42%' }}
          />
          <span style={{ fontSize: `${fontSize}px`, lineHeight: 1 }}>/</span>
          <input
            type="number"
            value={field._maxValue ?? ''}
            onChange={(e) => onChange({ type: 'max', value: Number(e.target.value) })}
            style={{ ...inputStyle, width: '42%' }}
          />
        </div>
      );
    }
    return (
      <div style={readStyle}>
        {displayVal} / {field._maxValue ?? '?'}
      </div>
    );
  }

  // ── Select ──
  if (type === 'select') {
    const ann = annMap?.[field.field_key];
    const options = ann?.validations?.options || [];
    if (editable) {
      return (
        <select
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          style={{ ...inputStyle, cursor: 'pointer' }}
        >
          <option value=""></option>
          {options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }
    return <div style={readStyle}>{value ?? ''}</div>;
  }

  // ── Repeatable list ──
  if (type === 'repeatable_list') {
    const displayStr = Array.isArray(value) ? value.join(', ') : (value ?? '');
    if (editable) {
      return (
        <input
          type="text"
          value={displayStr}
          onChange={(e) => {
            const arr = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
            onChange(arr);
          }}
          style={inputStyle}
        />
      );
    }
    return <div style={readStyle}>{displayStr}</div>;
  }

  // ── Text / Number / Dice value (défaut) ──
  const inputType = type === 'number' ? 'number' : 'text';
  const displayValue = value ?? '';

  if (editable) {
    return (
      <input
        type={inputType}
        value={displayValue}
        onChange={(e) => {
          const v = type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value;
          onChange(v);
        }}
        style={inputStyle}
      />
    );
  }
  return (
    <div style={readStyle}>
      {String(displayValue)}
    </div>
  );
}

// ── PDFFieldOverlay ─────────────────────────────────────────────────────────
/**
 * Overlay absolu au-dessus du canvas PDF.
 * Positionne chaque champ de la page courante par coordonnées normalisées (0..1).
 *
 * Props :
 *  - pageFields: array  — champs de pdf_overlay pour la page courante
 *  - annMap: object     — { fieldKey -> annotation } pour les permissions
 *  - formData: object   — data_json courant
 *  - onFieldChange: fn  — (newFormData) => void
 *  - isMJ: bool
 *  - isReadOnly: bool
 */
export default function PDFFieldOverlay({
  pageFields,
  annMap,
  formData,
  onFieldChange,
  isMJ,
  isReadOnly,
}) {
  if (!pageFields || pageFields.length === 0) return null;

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      {pageFields.map((field) => {
        const ann = annMap?.[field.field_key];
        const isGmOnly = ann?.permissions?.visibility === 'gm_only';
        const editableBy = ann?.permissions?.editable_by ?? 'player';

        // Masquer les champs GM aux joueurs
        if (isGmOnly && !isMJ) return null;

        const fieldEditable = !isReadOnly && (isMJ || editableBy === 'player');
        const value = getNestedValue(formData, field.field_key);

        // Pour resource_bar : enrichir le field avec la valeur max
        const enrichedField = { ...field };
        if (field.field_type === 'resource_bar' && field.resource_max_key) {
          enrichedField._maxValue = getNestedValue(formData, field.resource_max_key);
        }

        // Handler onChange adapté selon le type
        const handleChange = (newVal) => {
          if (!fieldEditable) return;

          if (field.field_type === 'resource_bar') {
            // newVal est { type: 'current'|'max', value: number }
            if (newVal.type === 'current') {
              onFieldChange(setNestedValue(formData, field.field_key, newVal.value));
            } else if (newVal.type === 'max' && field.resource_max_key) {
              onFieldChange(setNestedValue(formData, field.resource_max_key, newVal.value));
            }
          } else {
            onFieldChange(setNestedValue(formData, field.field_key, newVal));
          }
        };

        return (
          <div
            key={field.field_key}
            style={{
              position: 'absolute',
              left: pct(field.x),
              top: pct(field.y),
              width: pct(field.width),
              height: pct(field.height),
              pointerEvents: 'auto',
              // Indicateur visuel subtil pour les champs GM en mode MJ
              ...(isGmOnly && isMJ ? { outline: '1px dashed rgba(239,68,68,0.4)' } : {}),
            }}
          >
            <PDFField
              field={enrichedField}
              value={value}
              editable={fieldEditable}
              onChange={handleChange}
              annMap={annMap}
            />
          </div>
        );
      })}
    </div>
  );
}
